import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TransactionModal from '../components/TransactionModal';
import { useShop } from '../App';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Transactions() {
  const navigate = useNavigate();
  const { shops, currentShop } = useShop();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { toasts, success, error: toastError } = useToast();
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterLayaway, setFilterLayaway] = useState('any');
  const [filterClosed, setFilterClosed] = useState('any');
  const [filterBalance, setFilterBalance] = useState('any');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [quickRange, setQuickRange] = useState('');
  const [filterShopId, setFilterShopId] = useState(() => localStorage.getItem('activeShopId') || '');
  const [showingRecent, setShowingRecent] = useState(true);

  // Helper: format date as YYYY-MM-DD
  const formatDate = (d) => d.toISOString().split('T')[0];
  const today = new Date();

  // Quick date range handlers
  const setThisWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    setDateFrom(formatDate(sundayThisWeek));
    setDateTo(formatDate(today));
  };
  const setLastWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayLastWeek = new Date(sundayThisWeek);
    sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
    setDateFrom(formatDate(sundayLastWeek));
    setDateTo(formatDate(sundayThisWeek));
  };
  const setThisMonth = () => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(formatDate(monthStart));
    setDateTo(formatDate(today));
  };
  const setLastMonth = () => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setDateFrom(formatDate(lastMonthStart));
    setDateTo(formatDate(thisMonthStart));
  };
  const setThisQuarter = () => {
    const quarter = Math.floor(today.getMonth() / 3);
    const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
    setDateFrom(formatDate(quarterStart));
    setDateTo(formatDate(today));
  };
  const setLastQuarter = () => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
    const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
    setDateFrom(formatDate(lastQuarterStart));
    setDateTo(formatDate(thisQuarterStart));
  };
  const setThisYear = () => {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(yearStart));
    setDateTo(formatDate(today));
  };
  const setLastYear = () => {
    const lastYear = today.getFullYear() - 1;
    const lastYearStart = new Date(lastYear, 0, 1);
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(lastYearStart));
    setDateTo(formatDate(thisYearStart));
  };
  const setAllTime = () => {
    setDateFrom('2000-01-01');
    setDateTo(formatDate(today));
  };

  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    setQuickRange(range);
    const rangeHandlers = {
      'this-week': setThisWeek, 'last-week': setLastWeek,
      'this-month': setThisMonth, 'last-month': setLastMonth,
      'this-quarter': setThisQuarter, 'last-quarter': setLastQuarter,
      'this-year': setThisYear, 'last-year': setLastYear,
      'all-time': setAllTime,
    };
    if (rangeHandlers[range]) {
      rangeHandlers[range]();
    } else {
      setDateFrom('');
      setDateTo('');
    }
  };

  const resetFilters = () => {
    setFilterLayaway('any');
    setFilterClosed('any');
    setFilterBalance('any');
    setDateFrom('');
    setDateTo('');
    setQuickRange('');
    setFilterShopId(localStorage.getItem('activeShopId') || '');
    setShowFilters(false);
  };

  const toggleFilters = () => {
    if (showFilters) {
      resetFilters();
    } else {
      setShowFilters(true);
    }
  };

  const hasActiveFilters = () => {
    // Note: filterShopId is the default (from localStorage), not a user-applied filter
    return filterLayaway !== 'any' || filterClosed !== 'any' || filterBalance !== 'any'
      || dateFrom || dateTo;
  };

  // Initial fetch on mount (immediate, no debounce)
  useEffect(() => {
    setLoading(true);
    setShowingRecent(true);
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (filterShopId) params.set('shop_id', filterShopId);
    fetch(`${API_URL}/transactions?${params.toString()}`, {
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch transactions');
        return res.json();
      })
      .then(data => setTransactions(data))
      .catch(err => toastError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const initialShopRef = useRef(filterShopId);
  const mountedRef = useRef(false);

  // Debounced refetch when search or filters change (not on initial mount)
  useEffect(() => {
    // Skip on initial mount — the useEffect above handles that
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const debounceTimer = setTimeout(() => {
      if (searchQuery.length >= 3 || hasActiveFilters() || filterShopId !== initialShopRef.current) {
        setShowingRecent(false);
        fetchTransactions();
      } else if (searchQuery.length > 0 && searchQuery.length < 3) {
        setTransactions([]);
        setShowingRecent(false);
      } else if (!showingRecent) {
        // Search cleared and no filters - reload recent
        setShowingRecent(true);
        const fetchRecent = async () => {
          setLoading(true);
          try {
            const params = new URLSearchParams();
            params.set('limit', '100');
            if (filterShopId) params.set('shop_id', filterShopId);
            const res = await fetch(`${API_URL}/transactions?${params.toString()}`, {
              credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to fetch transactions');
            const data = await res.json();
            setTransactions(data);
          } catch (err) {
            toastError(err.message);
          } finally {
            setLoading(false);
          }
        };
        fetchRecent();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterLayaway, filterClosed, filterBalance, dateFrom, dateTo, filterShopId, showingRecent]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (searchQuery) params.set('search', searchQuery);
      if (filterLayaway !== 'any') params.set('is_layaway', filterLayaway);
      if (filterClosed !== 'any') params.set('is_closed', filterClosed);
      if (filterBalance !== 'any') params.set('balance_due', filterBalance);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (filterShopId) params.set('shop_id', filterShopId);
      const queryString = params.toString();
      const url = queryString ? `${API_URL}/transactions?${queryString}` : `${API_URL}/transactions`;
      
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (customer = null) => {
    setEditingTransaction(null);
    setSelectedCustomer(customer || null);
    setShowModal(true);
  };

  const openEditModal = (transaction) => {
    setEditingTransaction(transaction);
    setSelectedCustomer(transaction.customer ? {
      id: transaction.customer.id,
      first_name: transaction.first_name,
      last_name: transaction.last_name,
      mobile_phone: transaction.customer.mobile_phone,
      home_phone: transaction.customer.home_phone,
      work_phone: transaction.customer.work_phone,
      email: transaction.customer.email,
      address_line1: transaction.customer.address_line1,
      address_line2: transaction.customer.address_line2,
      city: transaction.customer.city,
      state: transaction.customer.state,
      postal_code: transaction.customer.postal_code
    } : null);
    setShowModal(true);
  };

  const handleModalSuccess = (transaction) => {
    success(editingTransaction ? 'Transaction updated successfully' : 'Transaction created successfully');
    setShowModal(false);
    setEditingTransaction(null);
    fetchTransactions();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTransaction(null);
    setSelectedCustomer(null);
    fetchTransactions();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const res = await fetch(`${API_URL}/transactions/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete transaction');
      }

      success('Transaction deleted successfully');
      fetchTransactions();
    } catch (err) {
      toastError(err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col px-4 py-1 sm:px-0">
      <div className="bg-white shadow rounded-lg flex flex-col flex-1 overflow-hidden">
        <div className="flex-shrink-0 p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
            <button
              onClick={()=>{openCreateModal()}}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Add Transaction
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search transactions by customer name, notes, or line item description..."
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              onClick={toggleFilters}
              className={`px-4 py-2 text-sm font-medium border rounded-md ${
                showFilters
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {showFilters ? '✕ Clear Filters' : '☰ Filters'}
            </button>
          </div>

          {/* Filter Bar */}
          {showFilters && (
          <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Shop:</span>
              <select
                value={filterShopId}
                onChange={(e) => setFilterShopId(e.target.value)}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Shops</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.shop_id}>{shop.shop_id}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Date:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Quick Range:</span>
              <select
                value={quickRange}
                onChange={handleQuickRangeChange}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Range</option>
                <option value="this-week">This Week</option>
                <option value="last-week">Last Week</option>
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="this-quarter">This Quarter</option>
                <option value="last-quarter">Last Quarter</option>
                <option value="this-year">This Year</option>
                <option value="last-year">Last Year</option>
                <option value="all-time">All Time</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Layaway:</span>
              <div className="flex rounded-md shadow-sm">
                {['any', 'true', 'false'].map(val => (
                  <button
                    key={val}
                    onClick={() => setFilterLayaway(val)}
                    className={`px-3 py-1 text-xs font-medium rounded-l-md first:rounded-l-md last:rounded-r-md border ${
                      filterLayaway === val
                        ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } ${val !== 'any' ? '-ml-px' : ''}`}
                  >
                    {val === 'any' ? 'Any' : val === 'true' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Closed:</span>
              <div className="flex rounded-md shadow-sm">
                {['any', 'true', 'false'].map(val => (
                  <button
                    key={val}
                    onClick={() => setFilterClosed(val)}
                    className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                      filterClosed === val
                        ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } ${val !== 'any' ? '-ml-px' : ''}`}
                  >
                    {val === 'any' ? 'Any' : val === 'true' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Balance:</span>
              <div className="flex rounded-md shadow-sm">
                {['any', 'paid', 'balance'].map(val => (
                  <button
                    key={val}
                    onClick={() => setFilterBalance(val)}
                    className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                      filterBalance === val
                        ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } ${val !== 'any' ? '-ml-px' : ''}`}
                  >
                    {val === 'any' ? 'Any' : val === 'paid' ? 'Paid' : 'Balance Due'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}
        </div>

        <ToastContainer toasts={toasts} />

        {loading && (
          <div className="flex-shrink-0 text-center py-4">
            <p>Loading...</p>
          </div>
        )}

        {!loading && showingRecent && (
          <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-6 py-2">
            <p className="text-sm text-blue-700 font-medium">Showing {transactions.length} most recent transactions</p>
          </div>
        )}

        {!loading && (
          <div className="flex-1 overflow-auto px-6 pb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                      {showingRecent
                        ? 'No transactions found'
                        : searchQuery.length >= 3 || hasActiveFilters()
                          ? 'No transactions found matching your search'
                          : 'No transactions found'}
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => {
                    // Backend returns customer fields flattened directly on the transaction object
                    const customerObj = {
                      id: transaction.customer_id,
                      first_name: transaction.first_name,
                      last_name: transaction.last_name,
                      mobile_phone: transaction.mobile_phone,
                      home_phone: transaction.home_phone,
                      work_phone: transaction.work_phone,
                      email: transaction.email,
                      address_line1: transaction.address_line1,
                      address_line2: transaction.address_line2,
                      city: transaction.city,
                      state: transaction.state,
                      postal_code: transaction.postal_code
                    };

                    return (
                    <tr
                      key={transaction.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedCustomer(customerObj);
                        setEditingTransaction(transaction);
                        setShowModal(true);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${transaction.customer_id}/dashboard`);
                          }}
                          className="text-indigo-700 hover:text-indigo-900 font-medium"
                          title={`Go to ${transaction.first_name} ${transaction.last_name}'s dashboard`}
                        >
                          {`${transaction.first_name} ${transaction.last_name}`}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transaction.shop_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {transaction.shop_id}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(() => {
                          const title = transaction.first_line_item_title;
                          const desc = transaction.first_line_item_description;
                          const detailsText = title || desc || 'Transaction';
                          return (
                            <span className="truncate block max-w-xs" title={detailsText}>
                              {detailsText.length > 50 ? detailsText.substring(0, 47) + '...' : detailsText}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(transaction.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {(() => {
                          const total = parseFloat(transaction.total_amount) || 0;
                          const paid = parseFloat(transaction.payments_sum) || 0;
                          const balance = total - paid;
                          return (
                            <span className={balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(balance)}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(transaction.status)}
                          {transaction.is_layaway && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                              Layaway
                            </span>
                          )}
                          {transaction.is_closed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                              Closed
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => window.open(`${API_URL}/transactions/${transaction.id}/pdf`, '_blank')}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                          title="Print Receipt"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                          Print
                        </button>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transaction Create/Edit Modal */}
      <TransactionModal
        isOpen={showModal}
        onClose={handleModalClose}
        customer={selectedCustomer}
        editingTransaction={editingTransaction}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
